import { InboxItem } from '../../services/api/inboxApi';
import { ActionItem } from '../../services/api/actionsApi';
import type { DiscussItemCommandPayload, DiscussSeed } from '../../services/chatStream';

/**
 * Feed model (chat-centric revamp §5/§6.4): one unified, read-mostly stream
 * composed from the inbox (reports) and open action items (todos/alerts).
 */

export interface FeedEntry {
  /** Stable row key; prefixes keep the two id spaces from colliding. */
  key: string;
  kind: 'inbox' | 'action';
  /** Spec §6.4 glyph: ☀ brief · 📄 report · 🔺 alert · ☐ todo. */
  icon: string;
  title: string;
  /** Inbox preview or "type · symbol" meta line. */
  meta: string | null;
  unread: boolean;
  createdAt: string;
  item: InboxItem | ActionItem;
}

export type FeedSection = { key: 'today' | 'earlier'; title: string; data: FeedEntry[] };

/** Spec §6.4: briefs vs reports vs alerts vs plain todos. */
export function feedIcon(kind: FeedEntry['kind'], item: InboxItem | ActionItem): string {
  if (kind === 'inbox') {
    return (item as InboxItem).type === 'morning_briefing' ? '☀' : '📄';
  }
  const actionType = (item as ActionItem).action_type;
  return actionType === 'review_stop' || actionType === 'review_target' ? '🔺' : '☐';
}

/** T7: alert rows are the ones that carry the 复盘 thesis shortcut. */
export function isAlertEntry(entry: FeedEntry): boolean {
  return (
    entry.kind === 'action' &&
    ((entry.item as ActionItem).action_type === 'review_stop' ||
      (entry.item as ActionItem).action_type === 'review_target')
  );
}

/** T20 (revamp §6.3/§6.4): the `discuss_item` payload a row's 💬 讨论 opens.
 * Inbox types map to brief/research (UUID string ids), action types to
 * alert/todo (numeric ids) — the same taxonomy feedIcon renders, and the same
 * id typing the backend's int|str union routes by. */
export function discussCommandOf(entry: FeedEntry): DiscussItemCommandPayload {
  if (entry.kind === 'inbox') {
    const item = entry.item as InboxItem;
    return {
      command: 'discuss_item',
      kind: item.type === 'morning_briefing' ? 'brief' : 'research',
      item_id: item.id,
    };
  }
  const item = entry.item as ActionItem;
  return {
    command: 'discuss_item',
    kind:
      item.action_type === 'review_stop' || item.action_type === 'review_target' ? 'alert' : 'todo',
    item_id: item.id,
  };
}

/** Session title for a Feed discussion (T20): `💬 {title truncated 24}`. */
export function discussSessionTitle(title: string): string {
  return `💬 ${title.slice(0, 24)}`;
}

/** The deferred discuss hand-off (revised 2026-10-09): the command to attach to
 * the user's first send, plus the row title/excerpt the chat's pending context
 * card shows. The Feed no longer auto-fires a turn — it docks the item as
 * context and lets the user type the opening question. */
export function discussSeedOf(entry: FeedEntry): DiscussSeed {
  return {
    command: discussCommandOf(entry),
    title: entry.title,
    excerpt: entry.meta,
  };
}

/** Merge both stores newest-first. `t` renders the action-type meta line. */
export function toFeedEntries(
  inboxItems: InboxItem[],
  actionItems: ActionItem[],
  t: (key: string, opts?: any) => string
): FeedEntry[] {
  const inboxEntries: FeedEntry[] = inboxItems.map((item) => ({
    key: `inbox-${item.id}`,
    kind: 'inbox',
    icon: feedIcon('inbox', item),
    title: item.title,
    meta: item.preview,
    unread: item.read_at === null,
    createdAt: item.created_at,
    item,
  }));
  const actionEntries: FeedEntry[] = actionItems.map((item) => ({
    key: `action-${item.id}`,
    kind: 'action',
    icon: feedIcon('action', item),
    title: item.title,
    meta: [t(`actions.type_${item.action_type}`, { defaultValue: item.action_type }), item.symbol]
      .filter(Boolean)
      .join(' · '),
    // read_at is optional on the wire until the Task-3 list field ships
    // everywhere — treat "never stamped" as unread.
    unread: !item.read_at,
    createdAt: item.created_at,
    item,
  }));
  return [...inboxEntries, ...actionEntries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Local-calendar Today vs Earlier grouping (spec §5). */
export function isToday(iso: string, now: Date = new Date()): boolean {
  const d = new Date(iso);
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function groupFeedSections(
  entries: FeedEntry[],
  labels: { today: string; earlier: string },
  now: Date = new Date()
): FeedSection[] {
  const today = entries.filter((e) => isToday(e.createdAt, now));
  const earlier = entries.filter((e) => !isToday(e.createdAt, now));
  const sections: FeedSection[] = [
    { key: 'today', title: labels.today, data: today },
    { key: 'earlier', title: labels.earlier, data: earlier },
  ];
  return sections.filter((s) => s.data.length > 0);
}
